import React, { useMemo, useState } from "react";
import {
  BadgeIndianRupee,
  FileCheck2,
  FileText,
  ReceiptText,
  UserPlus,
} from "lucide-react";

const firstValue = (record, keys, fallback = "") => {
  const key = keys.find((candidate) => record?.[candidate] !== undefined && record?.[candidate] !== null && record?.[candidate] !== "");
  return key ? record[key] : fallback;
};

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const date = new Date(value < 100000000000 ? value * 1000 : value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const text = String(value).trim();
  const indianDate = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(.*)$/);
  const normalized = indianDate
    ? `${indianDate[3]}-${indianDate[2].padStart(2, "0")}-${indianDate[1].padStart(2, "0")}${indianDate[4]}`
    : text;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const recordDate = (record, businessDateKeys) => {
  const value = firstValue(record, [
    "createdAt",
    "created_at",
    "createdDate",
    "created_date",
    "creationDate",
    "updatedAt",
    "updated_at",
    ...businessDateKeys,
    "date",
  ]);
  return parseDate(value);
};

const formatRelativeTime = (date) => {
  if (!date) return "Date unavailable";
  const elapsed = Math.max(0, Date.now() - date.getTime());
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const money = (value) => {
  const amount = Number(String(value ?? 0).replace(/[^0-9.-]/g, ""));
  return `₹ ${(Number.isFinite(amount) ? amount : 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const createActivity = (type, record, index) => {
  const customer = firstValue(record, ["customerName", "companyName", "displayName", "name"], "Unknown customer");
  const configs = {
    invoice: {
      icon: FileText,
      tone: "green",
      date: recordDate(record, ["invoiceDate"]),
      title: `Invoice ${firstValue(record, ["invoiceNumber", "invoice", "number"], "created")} created`,
      subtitle: `Customer: ${customer}`,
    },
    payment: {
      icon: BadgeIndianRupee,
      tone: "green",
      date: recordDate(record, ["paymentDate"]),
      title: `Payment received${firstValue(record, ["invoiceNumber", "invoice"]) ? ` for ${firstValue(record, ["invoiceNumber", "invoice"])}` : ""}`,
      subtitle: `Amount: ${money(firstValue(record, ["amountReceived", "payment", "amount", "total"]))}`,
    },
    customer: {
      icon: UserPlus,
      tone: "violet",
      date: recordDate(record, ["customerDate"]),
      title: "New customer added",
      subtitle: `Customer: ${customer}`,
    },
    quotation: {
      icon: FileCheck2,
      tone: "violet",
      date: recordDate(record, ["quoteDate", "quotationDate"]),
      title: `Quotation ${firstValue(record, ["quoteNumber", "quotationNumber", "number"], "created")} created`,
      subtitle: `Customer: ${customer}`,
    },
    expense: {
      icon: ReceiptText,
      tone: "orange",
      date: recordDate(record, ["expenseDate"]),
      title: `Expense ${firstValue(record, ["expenseNumber", "invoice", "reference"], "recorded")}`,
      subtitle: `${firstValue(record, ["expenseAccount", "category"], "Amount")}: ${money(firstValue(record, ["amount", "expenseAmount", "total"]))}`,
    },
  };
  const activity = configs[type];
  return { ...activity, id: `${type}-${firstValue(record, ["id", "invoiceNumber", "quoteNumber", "paymentCount"], "record")}-${index}` };
};

const RecentActivities = ({ records = {}, loading = false }) => {
  const [expanded, setExpanded] = useState(false);
  const activities = useMemo(() => Object.entries(records)
    .flatMap(([type, rows]) => (Array.isArray(rows) ? rows : []).map((row, index) => createActivity(type, row, index)))
    .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0)), [records]);
  const visibleActivities = expanded ? activities : activities.slice(0, 4);

  return (
    <section className="zarp-activity-card">
      <div className="zarp-section-header compact">
        <h2>Recent Activities</h2>
        {activities.length > 4 && (
          <button type="button" onClick={() => setExpanded((current) => !current)}>
            {expanded ? "Show less" : "View all"}
          </button>
        )}
      </div>

      <div className="zarp-activity-list">
        {loading && <p className="zarp-activity-empty">Loading recent activities...</p>}
        {!loading && visibleActivities.length === 0 && (
          <p className="zarp-activity-empty">No recent activity found.</p>
        )}
        {!loading && visibleActivities.map((activity) => {
          const Icon = activity.icon;
          return (
            <article className="zarp-activity-item" key={activity.id}>
              <span className={`zarp-activity-icon ${activity.tone}`}><Icon size={18} /></span>
              <span>
                <strong>{activity.title}</strong>
                <small>{activity.subtitle}</small>
              </span>
              <time dateTime={activity.date?.toISOString()}>{formatRelativeTime(activity.date)}</time>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default RecentActivities;
